// Finance → Accounting: connect the organisation's accounting system and map
// payroll's account codes onto its chart of accounts. One screen serves every
// provider — the name, and every sentence naming it, comes from /status.
//
// What this screen deliberately does NOT have: a Post button. Posting now
// exists, but it belongs on the run — you post a payroll run, not a
// connection — so it lives in AccountingPostCard on the run detail page.
// This screen's whole job is to get the mapping right BEFORE anything can be
// posted, so it shows, per row, exactly why a row is not usable (unmapped,
// deleted upstream, inactive, wrong currency) rather than a green tick that
// means "saved" and nothing more.
//
// Two rights, deliberately separate: accounting:map owns the MAPPING (the
// finance lead's job — it is their chart of accounts), while connecting or
// disconnecting the company's books stays a tenant-admin act (migration:admin).
// Someone with only accounting:read sees the same picture and is told so.
//
// Both are narrowed PER COMPANY by the server (company reach): the selector
// lists only the companies this person may see, `can_map` says where they may
// edit, and the connection controls need reach over every company — a
// controller trusted with one company cannot take down the connection others
// post through. The server enforces all of it; this screen only reflects it.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { AlertTriangle, CheckCircle2, Link2, Loader2, RefreshCw, Unlink } from 'lucide-react';
import { Card, CardContent } from '@/features/shared/components/ui/card';
import { Button } from '@/features/shared/components/ui/button';
import { Badge } from '@/features/shared/components/ui/badge';
import { Skeleton } from '@/features/shared/components/ui/skeleton';
import { useConfirm } from '@/features/shared/components/confirm/ConfirmProvider';
import { getApiErrorMessage } from '@/lib/apiError';
import EmployeeAdvanceAccounts from '@/features/payroll/components/EmployeeAdvanceAccounts';
import EntityAccountingPolicy from '@/features/payroll/components/EntityAccountingPolicy';
import EntityClassMapping from '@/features/payroll/components/EntityClassMapping';
import { blockersFirst, blockingNames, blockingRows } from './accountingMapping';
import {
  activateAccountingOAuth, disconnectAccounting, getAccountMappings, getAccountingCompanies,
  getAccountingStatus, getExternalAccounts, refreshExternalAccounts, setAccountMappings,
  startAccountingOAuth,
  type AccountingCompany, type AccountingProvider, type AccountingStatus, type ExternalAccount,
  type MappingRow, type MappingView,
} from '@/services/accountingService';

/** 404 from a company-scoped call: the company is not one this person may see
 *  (or does not exist). Shown as "Not found", never as an empty mapping. */
const isNotFound = (e: unknown): boolean =>
  (e as { response?: { status?: number } })?.response?.status === 404;

/** Human text for each reason a mapped row still cannot be posted with. */
export const problemLabel = (
  t: (k: string, o?: Record<string, unknown>) => string,
  row: MappingRow,
  system = 'the accounting system',
): string => {
  switch (row.problem) {
    case 'missing':
      return t('accounting.problemMissing', { defaultValue: 'Not mapped yet' });
    case 'unknown_account':
      return t('accounting.problemUnknown', {
        defaultValue: 'The mapped account is no longer in {{system}} — re-map it', system,
      });
    case 'inactive':
      return t('accounting.problemInactive', {
        defaultValue: 'That account is inactive in {{system}}', system,
      });
    case 'currency_mismatch':
      return t('accounting.problemCurrency', {
        defaultValue: 'That account is in {{cur}}, and this organisation does not have multicurrency on',
        cur: row.external_account_currency || '—',
      });
    default:
      return '';
  }
};

const SOURCE_LABEL: Record<string, string> = {
  payroll: 'Payroll',
  commission: 'Commission',
  expense_category: 'Expense category',
  orphaned: 'No longer in payroll',
};

interface Props {
  /** accounting:map — refresh the chart of accounts and edit the mapping. */
  canMap: boolean;
  /** migration:admin — connect or disconnect the organisation. */
  canConnect: boolean;
  /** Which accounting system. One panel serves them all. */
  provider?: AccountingProvider;
}

export const AccountingPanel: React.FC<Props> = ({ canMap, canConnect, provider = 'quickbooks' }) => {
  const { t } = useTranslation('payroll');
  const confirm = useConfirm();
  // Accounting is configured PER COMPANY. A group runs several, each with its
  // own chart and its own ledger, so everything below the selector belongs to
  // the chosen one — and the server refuses rather than guessing.
  // Only the companies the server says this person may see — never the whole
  // workspace's list, which is a different permission's reach.
  const [entities, setEntities] = useState<AccountingCompany[]>([]);
  const [entityId, setEntityId] = useState('');
  // Whether the company list has been ANSWERED, successfully or not. Without
  // this the panel waits for a company it may never get and spins for ever —
  // which is what it did when the list failed to load.
  const [entitiesAsked, setEntitiesAsked] = useState(false);
  // A failed company list is an ERROR, not "no companies": the two need
  // different sentences, and only one of them is fixed by trying again.
  const [entitiesError, setEntitiesError] = useState<string | null>(null);
  const [canManageConnection, setCanManageConnection] = useState(false);
  const [scoped, setScoped] = useState(false);
  // The chosen company answered 404: outside this person's reach, or gone.
  const [notFound, setNotFound] = useState(false);
  const [companiesNonce, setCompaniesNonce] = useState(0);
  // The entity's saved advance policy, raised by the policy card. Decides
  // whether the per-employee advance accounts are configurable at all.
  const [advanceMode, setAdvanceMode] = useState('');
  const [status, setStatus] = useState<AccountingStatus | null>(null);
  const [view, setView] = useState<MappingView | null>(null);
  const [accounts, setAccounts] = useState<ExternalAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  // Edits stay local until Save, which sends ONLY the rows that changed.
  const [draft, setDraft] = useState<Record<string, string>>({});
  // `t` must not be a dependency of the loader: i18n hands back a new function
  // identity on some renders, which would make the effect re-fetch forever.
  const tRef = useRef(t);
  tRef.current = t;

  // The company list is loaded once; the configuration reloads whenever the
  // chosen company changes.
  useEffect(() => {
    let stopped = false;
    setEntitiesError(null);
    getAccountingCompanies(provider)
      .then((list) => {
        if (stopped) return;
        setEntities(list.companies);
        setCanManageConnection(list.can_manage_connection);
        setScoped(list.scoped);
        // Pick the first so a single-company workspace never has to choose.
        setEntityId((cur) =>
          list.companies.some((c) => c.legal_entity_id === cur) ? cur : list.companies[0]?.legal_entity_id || '');
      })
      .catch((e) => {
        if (stopped) return;
        setEntitiesError(getApiErrorMessage(e, tRef.current('accounting.companiesFailed', {
          defaultValue: 'The list of companies could not be loaded.',
        })));
      })
      .finally(() => { if (!stopped) setEntitiesAsked(true); });
    return () => { stopped = true; };
  }, [provider, companiesNonce]);

  const company = entities.find((c) => c.legal_entity_id === entityId);
  // What THIS person may do HERE: the route permission AND the server's word
  // for this company. Hiding a control is a courtesy — the server refuses
  // regardless.
  const mayMap = canMap && Boolean(company?.can_map);
  const mayConnect = canConnect && canManageConnection;

  const load = useCallback(async () => {
    if (!entityId) {
      // Nothing to configure until we know the company — but stop the spinner
      // once the question has been answered, so an unreachable company list
      // shows a sentence instead of a spinner that never ends.
      if (entitiesAsked) setLoading(false);
      return;
    }
    setLoading(true);
    setNotFound(false);
    try {
      const st = await getAccountingStatus(provider, entityId);
      setStatus(st);
      if (st.connected || st.state === 'needs_reauth') {
        let gone = false;
        const [mv, accts] = await Promise.all([
          getAccountMappings(provider, entityId).catch((e) => {
            if (isNotFound(e)) gone = true;
            return null;
          }),
          getExternalAccounts(provider, entityId).catch(() => [] as ExternalAccount[]),
        ]);
        setNotFound(gone);
        setView(mv);
        setAccounts(accts);
      } else {
        setView(null);
        setAccounts([]);
      }
      setDraft({});
    } catch (e) {
      if (isNotFound(e)) {
        setNotFound(true);
        setStatus(null);
        setView(null);
      } else {
        toast.error(getApiErrorMessage(e, tRef.current('accounting.loadFailed', { defaultValue: 'Could not load the accounting connection' })));
      }
    } finally {
      setLoading(false);
    }
  }, [provider, entityId, entitiesAsked]);

  useEffect(() => { void load(); }, [load]);


  // Every sentence that names the accounting system reads it from /status, so
  // a Xero tenant is never told about QuickBooks.
  const systemName = status?.label || t('accounting.genericSystem', { defaultValue: 'the accounting system' });

  // Returning from the provider.
  //
  // The OAuth callback cannot check whether the person still holds the
  // permission — it is unauthenticated, because the provider redirects a
  // browser to it. So it leaves the connection authorized-but-unusable and
  // this request, which HAS a token, finishes it. Firing it automatically
  // means nobody sees an extra step; a de-privileged actor simply gets a 403
  // and the connection stays pending.
  //
  // Runs once per return: the marker is stripped from the URL so a reload
  // does not repeat it (harmless either way — activation is idempotent).
  const [returnHandled, setReturnHandled] = useState(false);
  useEffect(() => {
    if (returnHandled) return;
    const params = new URLSearchParams(window.location.search);
    const marker = params.get('accounting');
    if (!marker) return;
    setReturnHandled(true);
    const clean = () => {
      params.delete('accounting');
      params.delete('legalEntityId');
      const qs = params.toString();
      window.history.replaceState({}, '', window.location.pathname + (qs ? `?${qs}` : ''));
    };
    if (marker === 'denied' || marker === 'failed') {
      // The provider's own outcome. Said once, rather than leaving somebody
      // staring at a screen that looks unchanged.
      toast.error(marker === 'denied'
        ? t('accounting.oauthDenied', { defaultValue: 'Authorization was cancelled — nothing was connected.' })
        : t('accounting.oauthFailed', { defaultValue: 'That authorization could not be completed. Try connecting again.' }));
      clean();
      return;
    }
    if (marker !== 'authorized') { clean(); return; }
    const entity = params.get('legalEntityId') || entityId;
    if (!entity) { clean(); return; }
    void (async () => {
      try {
        await activateAccountingOAuth(provider, entity);
        toast.success(t('accounting.connectedNow', {
          defaultValue: 'Connected. {{system}} is ready to map.', system: systemName,
        }));
      } catch (e) {
        // The honest case this flow exists for: the person no longer has
        // permission to connect. The connection stays unusable and the panel
        // offers Finish connecting to whoever does.
        toast.error(getApiErrorMessage(e, t('accounting.activateFailed', {
          defaultValue: 'Authorized, but not finished — an administrator needs to activate this connection.',
        })));
      } finally {
        clean();
        void load();
      }
    })();
  }, [returnHandled, entityId, provider, systemName, load, t]);

  const changed = useMemo(() => {
    if (!view) return [];
    return view.rows
      .filter((r) => draft[r.logical_code] !== undefined && draft[r.logical_code] !== (r.external_account_id || ''))
      .map((r) => ({ logical_code: r.logical_code, external_account_id: draft[r.logical_code] }));
  }, [view, draft]);

  const connect = async () => {
    setBusy('connect');
    try {
      // The company is sent now, on an authenticated request, and rides the
      // signed OAuth state — the callback is unauthenticated and must not be
      // trusted with it.
      const url = await startAccountingOAuth(provider, entityId, window.location.href);
      window.location.assign(url);
    } catch (e) {
      toast.error(getApiErrorMessage(e, t('accounting.connectFailed', { defaultValue: 'Could not start the connection' })));
      setBusy(null);
    }
  };

  // Finish a connection the callback could only authorize. Normally fired
  // automatically on return; this is the manual path for when that failed —
  // which is the case the pending state exists for.
  const activate = async () => {
    setBusy('activate');
    try {
      await activateAccountingOAuth(provider, entityId);
      toast.success(t('accounting.connectedNow', {
        defaultValue: 'Connected. {{system}} is ready to map.', system: systemName,
      }));
      await load();
    } catch (e) {
      toast.error(getApiErrorMessage(e, t('accounting.activateFailed', {
        defaultValue: 'Authorized, but not finished — an administrator needs to activate this connection.',
      })));
    } finally {
      setBusy(null);
    }
  };

  const disconnect = async () => {
    const ok = await confirm({
      title: t('accounting.disconnectTitle', { defaultValue: 'Disconnect {{system}}?', system: systemName }),
      message: t('accounting.disconnectBody', {
        defaultValue:
          'The stored authorization is revoked at Intuit and removed here. Your account mapping is kept, and reconnecting the SAME company restores it.',
      }),
      confirmLabel: t('accounting.disconnect', { defaultValue: 'Disconnect' }),
      danger: true,
    });
    if (!ok) return;
    setBusy('disconnect');
    try {
      await disconnectAccounting(provider, entityId);
      toast.success(t('accounting.disconnected', { defaultValue: '{{system}} disconnected', system: systemName }));
      await load();
    } catch (e) {
      toast.error(getApiErrorMessage(e, t('accounting.disconnectFailed', { defaultValue: 'Could not disconnect' })));
    } finally {
      setBusy(null);
    }
  };

  const refresh = async () => {
    setBusy('refresh');
    try {
      const n = await refreshExternalAccounts(provider, entityId);
      toast.success(t('accounting.refreshed', { defaultValue: '{{n}} account(s) read from {{system}}', n, system: systemName }));
      await load();
    } catch (e) {
      toast.error(getApiErrorMessage(e, t('accounting.refreshFailed', { defaultValue: 'Could not read the chart of accounts' })));
    } finally {
      setBusy(null);
    }
  };

  const save = async () => {
    if (changed.length === 0) return;
    setBusy('save');
    try {
      const next = await setAccountMappings(provider, entityId, changed);
      setView(next);
      setDraft({});
      toast.success(t('accounting.saved', { defaultValue: '{{n}} mapping(s) saved', n: changed.length }));
    } catch (e) {
      if (isNotFound(e)) setNotFound(true);
      // The server names the offending code ("SALARY_EXPENSE: account 42 is not
      // in this chart of accounts") — show that, not ours.
      toast.error(getApiErrorMessage(e, t('accounting.saveFailed', { defaultValue: 'Could not save the mapping' })));
    } finally {
      setBusy(null);
    }
  };

  if (loading && !entitiesError) {
    return (
      <div className="space-y-3" data-testid="accounting-loading">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  // The company list failed. Said as a failure with a way to retry — an empty
  // screen here would read as "this workspace has no companies".
  if (entitiesError) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-sm" data-testid="accounting-companies-error" role="alert">
          <p className="text-foreground">{entitiesError}</p>
          <Button size="sm" variant="outline" className="mt-3" onClick={() => {
            setEntitiesAsked(false);
            setLoading(true);
            setCompaniesNonce((n) => n + 1);
          }}>
            {t('accounting.retry', { defaultValue: 'Try again' })}
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Accounting is configured per company, so without one there is nothing to
  // show — but say so, rather than render an empty screen that looks broken.
  if (!entityId) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-sm text-muted-foreground" data-testid="accounting-no-entity">
          {entities.length === 0
            ? scoped
              ? t('accounting.noReachedEntity', {
                  defaultValue:
                    'You do not have access to any company’s accounting in this workspace. Ask the workspace owner for access to the companies you look after.',
                })
              : t('accounting.noEntity', {
                  defaultValue:
                    'Add a company before connecting an accounting system — each company has its own ledger and mappings.',
                })
            : t('accounting.chooseEntity', { defaultValue: 'Choose a company to configure.' })}
        </CardContent>
      </Card>
    );
  }

  // Which company's ledger. A group chooses; a person limited to one company
  // is still told which one, so nobody edits a mapping without knowing whose.
  const selector = (entities.length > 1 || scoped) ? (
    <Card>
      <CardContent className="py-4 flex flex-wrap items-center gap-3">
        <div className="min-w-0">
          <div className="text-sm font-semibold text-foreground">
            {t('accounting.entity', { defaultValue: 'Company' })}
          </div>
          <div className="text-xs text-muted-foreground">
            {scoped
              ? t('accounting.entityHintScoped', {
                  defaultValue: 'Each company has its own ledger, accounts and mappings. Only the companies you have access to are listed.',
                })
              : t('accounting.entityHint', {
                  defaultValue: 'Each company has its own ledger, accounts and mappings.',
                })}
          </div>
        </div>
        {entities.length > 1 ? (
          <select
            className="ml-auto rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground"
            value={entityId}
            onChange={(e) => setEntityId(e.target.value)}
            aria-label={t('accounting.entity', { defaultValue: 'Company' })}
            data-testid="accounting-entity"
          >
            {entities.map((e) => (
              <option key={e.legal_entity_id} value={e.legal_entity_id}>{e.name}</option>
            ))}
          </select>
        ) : (
          <span className="ml-auto text-sm font-medium text-foreground" data-testid="accounting-entity-name">
            {company?.name}
          </span>
        )}
      </CardContent>
    </Card>
  ) : null;

  // The chosen company answered 404 — outside this person's reach, or gone.
  // Its own state, never an empty mapping that could read as "nothing mapped".
  if (notFound) {
    return (
      <div className="space-y-6" data-testid="accounting-panel">
        {selector}
        <Card>
          <CardContent className="py-8 text-center text-sm" data-testid="accounting-not-found" role="alert">
            <p className="font-semibold text-foreground">{t('accounting.notFound', { defaultValue: 'Not found' })}</p>
            <p className="mt-1 text-muted-foreground">
              {t('accounting.notFoundHelp', {
                defaultValue: 'This company’s accounting is not available to you — it may not exist, or you do not have access to it.',
              })}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Gates on `offered` (per tenant), not `configured` (per deployment) — this
  // is the deep-link path, so it is the last thing standing between a tenant
  // outside the launch allowlist and a Connect button that would 403.
  if (!status?.offered && !status?.connected) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-sm text-muted-foreground" data-testid="accounting-unavailable">
          {t('accounting.notConfigured', {
            defaultValue: 'No accounting connector is available for this workspace yet.',
          })}
        </CardContent>
      </Card>
    );
  }

  const usable = view?.rows.filter((r) => r.mapped && !r.problem).length ?? 0;

  return (
    <div className="space-y-6" data-testid="accounting-panel">
      {selector}
      {/* --- connection ------------------------------------------------------ */}
      <Card>
        <CardContent className="py-5 space-y-3">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="text-base font-semibold text-foreground">
                  {status?.label || t('accounting.title', { defaultValue: 'Accounting system' })}
                </h3>
                {status?.connected && !status.needs_reauth && (
                  <Badge variant="outline" className="text-success border-success/40">
                    {t('accounting.connected', { defaultValue: 'Connected' })}
                  </Badge>
                )}
                {status?.needs_reauth && (
                  <Badge variant="outline" className="text-warning border-warning/40" data-testid="needs-reauth">
                    {t('accounting.needsReauth', { defaultValue: 'Reconnect needed' })}
                  </Badge>
                )}
                {status?.state === 'pending_activation' && (
                  // Authorized at the provider, not usable. Normally invisible
                  // — the SPA activates on return — so seeing this means the
                  // activation did not happen, most likely because whoever
                  // authorized it no longer has permission to.
                  <Badge variant="outline" className="text-warning border-warning/40" data-testid="pending-activation">
                    {t('accounting.pendingActivation', { defaultValue: 'Not finished' })}
                  </Badge>
                )}
                {status?.environment === 'sandbox' && (
                  <Badge variant="outline">{t('accounting.sandbox', { defaultValue: 'Sandbox' })}</Badge>
                )}
              </div>
              {status?.state === 'pending_activation' && (
                <p className="mt-1 text-sm text-foreground">
                  {t('accounting.pendingActivationHelp', {
                    defaultValue:
                      'This company was authorized at {{system}} but the connection was never finished, so nothing can post to it. An administrator can finish it here.',
                    system: systemName,
                  })}
                </p>
              )}
              <p className="mt-1 text-sm text-muted-foreground">
                {status?.connected || status?.state === 'needs_reauth' ? (
                  <>
                    {status.organisation_name || t('accounting.unnamedCompany', { defaultValue: 'Connected organisation' })}
                    {status.external_org_id ? ` · ${status.external_org_id}` : ''}
                    {status.home_currency ? ` · ${status.home_currency}` : ''}
                    {status.multicurrency_enabled
                      ? ` · ${t('accounting.multicurrency', { defaultValue: 'multicurrency on' })}`
                      : ''}
                  </>
                ) : (
                  t('accounting.notConnected', {
                    defaultValue: 'Nothing connected. Connecting only reads your chart of accounts — nothing is written back.',
                  })
                )}
              </p>
              {/* "Connected" in green with an empty chart is a reassuring state
                  that has not been earned: nothing can be mapped and nothing
                  can post until the accounts are pulled. A reviewer read the
                  card as healthy and reported only the missing name and sync
                  time — the actual problem was that the chart had never
                  loaded at all. */}
              {status?.connected && (status.account_count ?? 0) === 0 && (
                <p className="mt-1 flex items-start gap-1.5 text-sm text-warning" data-testid="chart-never-synced">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                  <span>
                    {t('accounting.chartNeverSynced', {
                      defaultValue:
                        'No accounts have been loaded from {{system}} yet, so nothing can be mapped. Use “Refresh chart of accounts”.',
                      system: systemName,
                    })}
                  </span>
                </p>
              )}
              {status?.last_error && (
                <p className="mt-1 text-xs text-danger" role="alert">{status.last_error}</p>
              )}
            </div>
            <div className="flex shrink-0 flex-wrap gap-2">
              {mayConnect && status?.state === 'pending_activation' && (
                <Button size="sm" onClick={activate} disabled={busy === 'activate'} data-testid="qbo-activate">
                  {busy === 'activate' ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Link2 className="mr-1 h-4 w-4" />}
                  {t('accounting.finishConnecting', { defaultValue: 'Finish connecting' })}
                </Button>
              )}
              {mayConnect && !status?.connected && status?.state !== 'pending_activation' && (
                <Button size="sm" onClick={connect} disabled={busy === 'connect'} data-testid="qbo-connect">
                  {busy === 'connect' ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Link2 className="mr-1 h-4 w-4" />}
                  {t('accounting.connect', { defaultValue: 'Connect {{system}}', system: systemName })}
                </Button>
              )}
              {mayConnect && status?.needs_reauth && (
                <Button size="sm" onClick={connect} disabled={busy === 'connect'} data-testid="qbo-reconnect">
                  <Link2 className="mr-1 h-4 w-4" />
                  {t('accounting.reconnect', { defaultValue: 'Reconnect' })}
                </Button>
              )}
              {mayMap && status?.connected && (
                <Button size="sm" variant="outline" onClick={refresh} disabled={busy === 'refresh'} data-testid="qbo-refresh">
                    {busy === 'refresh' ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-1 h-4 w-4" />}
                  {t('accounting.refreshAccounts', { defaultValue: 'Refresh chart of accounts' })}
                </Button>
              )}
              {mayConnect && status?.connected && (
                <Button size="sm" variant="outline" onClick={disconnect} disabled={busy === 'disconnect'} data-testid="qbo-disconnect">
                  <Unlink className="mr-1 h-4 w-4" />
                  {t('accounting.disconnect', { defaultValue: 'Disconnect' })}
                </Button>
              )}
            </div>
          </div>
          {status?.needs_reauth && status.reauth_by && (
            <p className="text-xs text-muted-foreground">
              {t('accounting.reauthBy', {
                defaultValue: 'The authorization expires on {{date}}. Reconnect before then — nothing is lost, but reads stop.',
                date: new Date(status.reauth_by).toLocaleDateString(),
              })}
            </p>
          )}
          {!mayMap && (
            <p className="text-xs text-muted-foreground" data-testid="accounting-readonly">
              {canMap
                ? t('accounting.readOnlyCompany', {
                    defaultValue: 'You can see this company’s connection and mapping. Changing its mapping needs mapping access for this company.',
                  })
                : t('accounting.readOnly', {
                    defaultValue: 'You can see the connection and its mapping. Changing the mapping needs the accounting mapping permission.',
                  })}
            </p>
          )}
          {canConnect && !canManageConnection && (
            // Why the connection buttons are missing for someone who holds the
            // connection permission: the connection is shared by every company.
            <p className="text-xs text-muted-foreground" data-testid="accounting-connection-locked">
              {t('accounting.connectionLocked', {
                defaultValue:
                  'Connecting, reconnecting or disconnecting {{system}} affects every company in this workspace, so only someone with access to every company (the workspace owner) can do it.',
                system: systemName,
              })}
            </p>
          )}
        </CardContent>
      </Card>

      {/* --- mapping ---------------------------------------------------------- */}
      {view && (
        <Card>
          <CardContent className="py-5 space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h3 className="text-base font-semibold text-foreground">
                  {t('accounting.mappingTitle', { defaultValue: 'Account mapping' })}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {t('accounting.mappingHelp', {
                    defaultValue:
                      'Each payroll account code needs an account in {{system}}. {{usable}} of {{total}} are ready.',
                    usable, total: view.rows.length, system: systemName,
                  })}
                </p>
              </div>
              {mayMap && (
                <Button size="sm" onClick={save} disabled={changed.length === 0 || busy === 'save'} data-testid="qbo-save-mappings">
                  {busy === 'save' ? <Loader2 className="h-4 w-4 animate-spin" /> : t('accounting.saveMapping', { defaultValue: 'Save {{n}} change(s)', n: changed.length })}
                </Button>
              )}
            </div>

            {!view.catalog_available && (
              <div role="alert" className="rounded-md border border-warning/40 bg-warning-soft p-3 text-xs text-foreground" data-testid="catalog-unavailable">
                {t('accounting.catalogUnavailable', {
                  defaultValue:
                    'Payroll’s list of account codes could not be read, so this shows only what is already mapped — it cannot tell you what is missing.',
                })}
              </div>
            )}
            {view.catalog_available && view.ready && (
              <div className="flex items-center gap-2 text-sm text-success" data-testid="mapping-ready">
                <CheckCircle2 className="h-4 w-4" aria-hidden />
                {t('accounting.ready', { defaultValue: 'Every required account is mapped.' })}
              </div>
            )}
            {view.catalog_available && !view.ready && (
              <div className="flex items-start gap-2 text-sm text-warning" data-testid="mapping-not-ready">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                <span>
                  {t('accounting.notReady', {
                    defaultValue:
                      'Not ready yet: {{n}} required account(s) still need an account in {{system}} — {{names}}. They are listed first below.',
                    n: blockingRows(view.rows).length,
                    names: blockingNames(view.rows),
                    system: systemName,
                  })}
                </span>
              </div>
            )}

            <div className="overflow-x-auto rounded-md border border-border">
              <table className="w-full text-sm">
                <thead className="bg-muted text-xs text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium">{t('accounting.colAccount', { defaultValue: 'Payroll account' })}</th>
                    <th className="px-3 py-2 text-left font-medium">{t('accounting.colSource', { defaultValue: 'Where from' })}</th>
                    <th className="px-3 py-2 text-left font-medium">{t('accounting.colExternal', { defaultValue: '{{system}} account', system: systemName })}</th>
                    <th className="px-3 py-2 text-left font-medium">{t('accounting.colState', { defaultValue: 'State' })}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {blockersFirst(view.rows).map((row) => {
                    const value = draft[row.logical_code] ?? row.external_account_id ?? '';
                    const problem = problemLabel(t, row, systemName);
                    return (
                      <tr key={row.logical_code} data-testid={`map-row-${row.logical_code}`}>
                        <td className="px-3 py-2">
                          <div className="text-foreground">
                            {row.label || row.logical_code}
                            {row.required && (
                              <span className="ml-1 text-danger" title={t('accounting.requiredHint', { defaultValue: 'Every payroll journal touches this account' })}>*</span>
                            )}
                          </div>
                          <div className="text-[11px] text-muted-foreground">
                            {row.logical_code}{row.gl_code ? ` · ${row.gl_code}` : ''}{row.dr_cr ? ` · ${row.dr_cr}` : ''}
                          </div>
                        </td>
                        <td className="px-3 py-2 text-muted-foreground">{SOURCE_LABEL[row.source || ''] || row.source || '—'}</td>
                        <td className="px-3 py-2">
                          {mayMap ? (
                            <select
                              aria-label={t('accounting.selectFor', { defaultValue: '{{system}} account for {{name}}', system: systemName, name: row.label || row.logical_code })}
                              className="h-8 w-full min-w-[14rem] rounded-md border border-border bg-background px-2 text-xs text-foreground"
                              value={value}
                              disabled={busy === 'save'}
                              onChange={(e) => setDraft((d) => ({ ...d, [row.logical_code]: e.target.value }))}
                            >
                              <option value="">{t('accounting.unmapped', { defaultValue: '— not mapped —' })}</option>
                              {accounts.map((a) => (
                                <option key={a.ID} value={a.ID} disabled={!a.Active}>
                                  {a.FullyQualifiedName || a.Name}
                                  {a.AccountType ? ` · ${a.AccountType}` : ''}
                                  {a.Active ? '' : ` · ${t('accounting.inactive', { defaultValue: 'inactive' })}`}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <span className="text-foreground">{row.external_account_name || '—'}</span>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          {problem ? (
                            <span className={row.problem === 'missing' ? 'text-muted-foreground' : 'text-warning'}>{problem}</span>
                          ) : (
                            <span className="text-success">{t('accounting.rowReady', { defaultValue: 'Ready' })}</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {view.rows.length === 0 && (
                    <tr>
                      <td colSpan={4} className="px-3 py-6 text-center text-muted-foreground">
                        {t('accounting.noAccounts', {
                          defaultValue: 'Nothing to map yet — refresh the chart of accounts first.',
                        })}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <p className="text-xs text-muted-foreground">
              {t('accounting.postingLivesOnTheRun', {
                defaultValue: 'Mapping only. Journals are posted to {{system}} from the payroll run itself, by finance, one run at a time — nothing posts automatically.',
                system: systemName,
              })}
              {status?.accounts_synced_at && (
                <>
                  {' · '}
                  {t('accounting.lastSynced', {
                    defaultValue: 'Chart of accounts read {{when}}',
                    when: new Date(status.accounts_synced_at).toLocaleString(),
                  })}
                </>
              )}
            </p>
          </CardContent>
        </Card>
      )}

      {/* This company's accounting policy — what decides a class, and which
          date a journal carries. Shown whenever a company is selected, not
          only once connected: the rules are a legitimate thing to set before
          the OAuth dance, and posting refuses until they exist. */}
      {entityId && (
        <EntityAccountingPolicy
          canMap={mayMap} systemName={systemName} provider={provider} legalEntityId={entityId}
          onAdvanceModeChange={setAdvanceMode}
        />
      )}

      {/* Per-employee advance accounts, only for the company that posts
          advances that way. Shown for the SAVED policy, not a draft one:
          binding accounts for a mode nobody chose would be busywork. */}
      {entityId && status?.connected && advanceMode === 'employee_subaccount' && (
        <EmployeeAdvanceAccounts
          canMap={mayMap} systemName={systemName} provider={provider} legalEntityId={entityId}
        />
      )}

      {/* Classes. Hides itself unless the ledger has the dimension. */}
      {status?.connected && (
        <EntityClassMapping canMap={mayMap} systemName={systemName} provider={provider} legalEntityId={entityId} />
      )}

      {status?.connected && !view && (
        <Card>
          <CardContent className="py-6 text-sm text-muted-foreground">
            {t('accounting.mappingUnavailable', { defaultValue: 'The mapping could not be loaded. Try again shortly.' })}
            <Button size="sm" variant="outline" className="ml-2" onClick={() => void load()}>
              {t('accounting.retry', { defaultValue: 'Try again' })}
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AccountingPanel;
